import {
  BufferAttribute,
  BufferGeometry,
  Euler,
  Mesh,
  Vector3,
  type ShaderMaterial,
} from 'three';
import { mulberry32, range, sign, type RandomSource } from '../random.js';
import {
  applyCoverFit,
  createMirrorMaterial,
  setMirrorTexture,
} from './MirrorMaterial.js';
import { MirrorVideoSource } from './MirrorVideoSource.js';

/**
 * One piece of glass with a music video sealed inside it.
 *
 * Everything about a shard — its outline, where it sits, how fast it tumbles, and
 * which second of the MV it shows — is derived from a single integer seed, so a
 * given seed reproduces the identical arrangement on every reload. That is what
 * makes the drift tunable and the luminance measurements comparable.
 */

/** Mutable drift state. Owned by the shard, advanced by `MirrorDriftController`. */
export interface MirrorShardState {
  readonly position: Vector3;
  readonly velocity: Vector3;
  readonly rotation: Euler;
  readonly angularVelocity: Vector3;
  /** Phase offsets for the low-frequency wander, one per axis. */
  readonly wanderPhase: Vector3;
  /** Frequencies for the same, all mutually irrational. */
  readonly wanderFrequency: Vector3;
  readonly wanderAmplitude: number;
  /**
   * Where the shard wants to sit, expressed in **screen** terms rather than world
   * ones: an angle around the frame and a radius in units of the half-viewport.
   *
   * "Screen periphery" cannot be expressed as a world-space ring. A circle of
   * fixed world radius projects to an ellipse in a 16:9 frame, so shards placed
   * near its top and bottom fall off screen entirely while the sides sit well
   * inside — which is exactly what the first attempt did, leaving two of four
   * invisible. Anchoring in normalised screen space adapts to aspect ratio and to
   * the shard's own depth for free.
   */
  anchorAngle: number;
  readonly anchorRadius: number;
  /** Radians per second the anchor creeps around the frame. Very slow. */
  readonly anchorAngularSpeed: number;
  /** Seconds spent obscuring the centre. Drives the eviction push. */
  centralDwell: number;
}

/**
 * The window every shard's playback rate is drawn from.
 *
 * Floored at 1 deliberately. The previous span reached down to 0.76, which put
 * four of the six shards into slow motion — and a drifting fragment of video
 * that is *also* running slow reads as a still image rather than as a moving
 * one. The whole span sits at or above real time now, so the motion is legible
 * without any shard looking sped up.
 *
 * The width is what desynchronises them; it is unchanged at 0.40, so the six
 * shards keep exactly the spread of rates they had relative to each other.
 */
export const MINIMUM_PLAYBACK_RATE = 1.0;
export const MAXIMUM_PLAYBACK_RATE = 1.4;

export interface MirrorShardPlayback {
  readonly playbackRate: number;
  readonly offsetSeconds: number;
}

export interface MirrorShardOptions {
  readonly seed: number;
  readonly index: number;
  /** Radius of the play field, so shards can be kept clear of it. */
  readonly fieldRadius: number;
}

export class MirrorShard {
  readonly mesh: Mesh;
  readonly material: ShaderMaterial;
  readonly video: MirrorVideoSource;
  readonly state: MirrorShardState;
  readonly playback: MirrorShardPlayback;
  readonly seed: number;
  /**
   * Per-shard deviation from the video's own proportions, roughly 0.8-1.25.
   *
   * The shard's world aspect is this times the video aspect, so the cover-fit
   * crop can never exceed this factor. Fixing the outline's proportions at build
   * time instead would be a trap: these MVs are 1080x1920, and landscape shards
   * cropped them to a 31% letterbox slice.
   */
  readonly shapeAspect: number;

  private readonly geometry: BufferGeometry;
  private readonly size: number;

  constructor(options: MirrorShardOptions) {
    const seed = options.seed + options.index * 9176;
    this.seed = seed;
    const random = mulberry32(seed);

    const shape = buildShardGeometry(random);
    this.geometry = shape.geometry;
    this.shapeAspect = shape.aspect;

    // Playback conditions. The rate spread is what desynchronises the shards;
    // the span is kept narrow so no shard reads as an obviously wrong half- or
    // double-speed copy of its neighbours.
    this.playback = {
      playbackRate: range(random, MINIMUM_PLAYBACK_RATE, MAXIMUM_PLAYBACK_RATE),
      // Offsets are spread across a long window so shards show genuinely
      // different sections; the real duration wraps this at load time.
      offsetSeconds: range(random, 0, 90),
    };

    this.material = createMirrorMaterial({
      edgeMix: random(),
      // A little per-shard variation stops the set reading as clones.
      brightness: range(random, 0.85, 1.15),
      opacity: range(random, 0.8, 1),
    });

    this.video = new MirrorVideoSource(this.playback);

    this.size = range(random, 5.2, 9.4);
    this.mesh = new Mesh(this.geometry, this.material);
    // Provisional until the video's proportions are known; `attachVideo` replaces
    // it so the glass matches whatever aspect the MV turns out to be.
    this.mesh.scale.set(this.size, this.size, 1);
    // Shards live behind the mandala and are never allowed to occlude it; see
    // MirrorDriftView for why this render order is a hard requirement.
    this.mesh.renderOrder = 8;
    this.mesh.frustumCulled = true;

    this.state = buildDriftState(random, options.fieldRadius);
    this.mesh.position.copy(this.state.position);
    this.mesh.rotation.copy(this.state.rotation);
  }

  /**
   * Binds the decoded video and reshapes the glass to suit it.
   *
   * The shard takes the video's aspect times its own deviation, which keeps the
   * silhouettes varied while bounding the crop to that deviation. Without this a
   * vertical MV inside a wide shard shows a thin horizontal band of itself.
   */
  attachVideo(): void {
    const texture = this.video.texture;
    if (texture === null) return;
    setMirrorTexture(this.material, texture);

    const videoAspect = this.video.aspect;
    if (videoAspect === null) return;
    this.mesh.scale.set(this.size * videoAspect, this.size, 1);
    applyCoverFit(this.material, videoAspect, videoAspect * this.shapeAspect);
  }

  /** Copies drift state onto the transform. Called once per frame. */
  syncTransform(): void {
    this.mesh.position.copy(this.state.position);
    this.mesh.rotation.copy(this.state.rotation);
  }

  dispose(): void {
    this.video.dispose();
    this.geometry.dispose();
    this.material.dispose();
  }
}

interface ShardShape {
  readonly geometry: BufferGeometry;
  readonly aspect: number;
}

/**
 * Builds an irregular convex polygon as a triangle fan.
 *
 * The fan gives a free rim-distance ramp: the centroid carries `aEdge = 1` and
 * every rim vertex carries 0, so interpolation hands the fragment shader a usable
 * approximation of distance-to-edge without a texture or a signed-distance pass.
 */
function buildShardGeometry(random: RandomSource): ShardShape {
  const sides = Math.floor(range(random, 5, 8.999));
  // Deviation from the video's proportions, not an absolute shape. See
  // `MirrorShard.shapeAspect` for why this is bounded so tightly.
  const aspect = range(random, 0.82, 1.24);

  const xs: number[] = [];
  const ys: number[] = [];
  for (let index = 0; index < sides; index += 1) {
    // Even spacing plus jitter keeps the polygon convex while making every shard
    // a different silhouette; pure random angles produce degenerate slivers.
    const angle =
      (index / sides) * Math.PI * 2 + range(random, -0.28, 0.28) / sides * Math.PI * 2;
    const radius = range(random, 0.72, 1.12);
    xs.push(Math.cos(angle) * radius * aspect);
    ys.push(Math.sin(angle) * radius);
  }

  const halfWidth = Math.max(...xs.map(Math.abs));
  const halfHeight = Math.max(...ys.map(Math.abs));

  const vertexCount = sides + 1;
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const uvs = new Float32Array(vertexCount * 2);
  const edges = new Float32Array(vertexCount);

  // Vertex 0 is the centroid.
  positions[0] = 0;
  positions[1] = 0;
  positions[2] = 0;
  uvs[0] = 0.5;
  uvs[1] = 0.5;
  edges[0] = 1;

  for (let index = 0; index < sides; index += 1) {
    const vertex = index + 1;
    const x = xs[index] as number;
    const y = ys[index] as number;
    positions[vertex * 3] = x;
    positions[vertex * 3 + 1] = y;
    positions[vertex * 3 + 2] = 0;
    uvs[vertex * 2] = x / (2 * halfWidth) + 0.5;
    uvs[vertex * 2 + 1] = y / (2 * halfHeight) + 0.5;
    edges[vertex] = 0;
  }

  for (let index = 0; index < vertexCount; index += 1) {
    normals[index * 3 + 2] = 1;
  }

  const indices = new Uint16Array(sides * 3);
  for (let index = 0; index < sides; index += 1) {
    indices[index * 3] = 0;
    indices[index * 3 + 1] = index + 1;
    indices[index * 3 + 2] = ((index + 1) % sides) + 1;
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setAttribute('aEdge', new BufferAttribute(edges, 1));
  geometry.setIndex(new BufferAttribute(indices, 1));
  geometry.computeBoundingSphere();

  return { geometry, aspect: halfWidth / halfHeight };
}

function buildDriftState(random: RandomSource, fieldRadius: number): MirrorShardState {
  // Depth is kept nearer than the first attempt: at 45-75 units away the shards
  // were a fraction of a percent of the frame, which reads as nothing at all.
  const angle = random() * Math.PI * 2;
  const depth = range(random, -32, -5);
  // Beyond the mandala's own screen footprint, inside the frame edge.
  const anchorRadius = range(random, 0.62, 1.02);
  const startRadius = Math.max(fieldRadius * 1.25, anchorRadius * 16);

  return {
    position: new Vector3(Math.cos(angle) * startRadius, Math.sin(angle) * startRadius, depth),
    // Slow enough that a shard takes tens of seconds to cross its own width.
    velocity: new Vector3(
      range(random, 0.04, 0.16) * sign(random),
      range(random, 0.03, 0.12) * sign(random),
      range(random, 0.02, 0.10) * sign(random),
    ),
    rotation: new Euler(
      random() * Math.PI * 2,
      random() * Math.PI * 2,
      random() * Math.PI * 2,
    ),
    angularVelocity: new Vector3(
      range(random, 0.004, 0.030) * sign(random),
      range(random, 0.004, 0.034) * sign(random),
      range(random, 0.003, 0.022) * sign(random),
    ),
    wanderPhase: new Vector3(
      random() * Math.PI * 2,
      random() * Math.PI * 2,
      random() * Math.PI * 2,
    ),
    // Periods of roughly 20-90 s, sharing no common multiple.
    wanderFrequency: new Vector3(
      range(random, 0.011, 0.049),
      range(random, 0.013, 0.052),
      range(random, 0.009, 0.041),
    ),
    wanderAmplitude: range(random, 0.10, 0.30),
    anchorAngle: angle,
    anchorRadius,
    // A full circuit of the frame takes four to fifteen minutes.
    anchorAngularSpeed: range(random, 0.007, 0.026) * sign(random),
    centralDwell: 0,
  };
}
